import React, { useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, Maximize2, Move } from "lucide-react";
import { assetUrl } from "../../lib/api";

/**
 * Lightweight pan + zoom viewer for a plan image (no external deps).
 * Wheel to zoom, drag to pan, buttons for zoom/reset.
 */
const PlanImageViewer = ({ src, alt = "", className = "", height = "h-[68vh]" }) => {
  const wrap = useRef(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const clampScale = (s) => Math.min(6, Math.max(1, s));

  const onWheel = useCallback((e) => {
    e.preventDefault();
    setScale((s) => {
      const ns = clampScale(s - e.deltaY * 0.0016 * s);
      if (ns === 1) setPos({ x: 0, y: 0 });
      return ns;
    });
  }, []);

  const onDown = (e) => {
    if (scale === 1) return;
    const pt = e.touches ? e.touches[0] : e;
    drag.current = { x: pt.clientX - pos.x, y: pt.clientY - pos.y };
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const pt = e.touches ? e.touches[0] : e;
    setPos({ x: pt.clientX - drag.current.x, y: pt.clientY - drag.current.y });
  };
  const onUp = () => (drag.current = null);
  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  if (!src) return null;

  return (
    <div className={`relative rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0C1510] ${className}`}>
      <div
        ref={wrap}
        className={`w-full ${height} overflow-hidden select-none ${scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}
        onWheel={onWheel}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
        data-testid="plan-viewer"
      >
        <img
          src={assetUrl(src)}
          alt={alt}
          draggable={false}
          className="w-full h-full object-contain transition-transform duration-100 will-change-transform"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
        />
      </div>

      <div className="absolute bottom-3 right-3 flex flex-col gap-1.5">
        <button onClick={() => setScale((s) => clampScale(s + 0.5))} data-testid="plan-zoom-in" className="w-9 h-9 rounded-lg bg-[#12201A]/85 border border-[#F3EEE3]/15 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><ZoomIn size={16} /></button>
        <button onClick={() => setScale((s) => { const n = clampScale(s - 0.5); if (n === 1) setPos({ x: 0, y: 0 }); return n; })} data-testid="plan-zoom-out" className="w-9 h-9 rounded-lg bg-[#12201A]/85 border border-[#F3EEE3]/15 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><ZoomOut size={16} /></button>
        <button onClick={reset} data-testid="plan-reset" className="w-9 h-9 rounded-lg bg-[#12201A]/85 border border-[#F3EEE3]/15 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><Maximize2 size={15} /></button>
      </div>
      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[0.62rem] text-[#F3EEE3]/70 bg-[#12201A]/70 backdrop-blur rounded-full px-3 py-1.5 pointer-events-none">
        <Move size={12} /> Scroll to zoom · drag to pan
      </div>
    </div>
  );
};

export default PlanImageViewer;
